import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

/** Store review of incoming price and rebate changes (flag `price_change_review`; Estimate row B38, #490). */
export type PriceChangeReviewSetting = 'off' | 'require_approval';
export type PriceChangeSource = 'master_map' | 'org_sheet' | 'rebate_program' | 'sale_window' | 'vendor_private_sheet';
export type PriceChangeStatus = 'pending' | 'approved' | 'declined' | 'pushed';

export interface PriceChangeSnapshot {
  price: number | null;
  compareAt: number | null;
  instantRebate: number | null;
}

export interface PriceChangeReviewItem {
  id: string;
  productId: string;
  integrationId: string;
  integrationName: string | null;
  source: PriceChangeSource;
  sourceId: string;
  effectiveAt: string | null;
  before: PriceChangeSnapshot;
  after: PriceChangeSnapshot;
  status: PriceChangeStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string | null;
  brand: string | null;
  model: string | null;
  sku: string | null;
  name: string | null;
  pushedAt: string | null;
}

export interface PriceChangeReviewList {
  items: PriceChangeReviewItem[];
  pendingCount: number;
}

export const PRICE_CHANGE_REVIEW_SETTING_KEY = ['store-price-change-review-setting'] as const;
export const PRICE_CHANGE_REVIEW_ITEMS_KEY = ['store-price-change-review-items'] as const;

async function readJson(response: Response, fallback: string): Promise<any> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || fallback);
  }
  return body;
}

const jsonHeaders = async () => ({ ...(await getAuthHeaders()), 'Content-Type': 'application/json' });

export async function fetchPriceChangeReviewSetting(fallback: string): Promise<PriceChangeReviewSetting> {
  const body = await readJson(await makeApiCall('/store/settings/price-change-review', { headers: await getAuthHeaders() }), fallback);
  return body.data.setting as PriceChangeReviewSetting;
}

export async function savePriceChangeReviewSetting(setting: PriceChangeReviewSetting, fallback: string): Promise<PriceChangeReviewSetting> {
  const body = await readJson(
    await makeApiCall('/store/settings/price-change-review', {
      method: 'PUT',
      headers: await jsonHeaders(),
      body: JSON.stringify({ setting }),
    }),
    fallback,
  );
  return body.data.setting as PriceChangeReviewSetting;
}

export async function fetchPriceChangeReviewItems(status: PriceChangeStatus | 'all', fallback: string): Promise<PriceChangeReviewList> {
  const body = await readJson(
    await makeApiCall(`/store/price-change-review/items?status=${encodeURIComponent(status)}`, { headers: await getAuthHeaders() }),
    fallback,
  );
  return { items: (body.data || []) as PriceChangeReviewItem[], pendingCount: Number(body.pendingCount) || 0 };
}

export async function decidePriceChange(id: string, decision: 'approve' | 'decline', fallback: string): Promise<void> {
  await readJson(
    await makeApiCall(`/store/price-change-review/items/${encodeURIComponent(id)}/${decision}`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({}),
    }),
    fallback,
  );
}

export async function approveAllPriceChanges(brand: string | null, fallback: string): Promise<number> {
  const body = await readJson(
    await makeApiCall('/store/price-change-review/approve-all', {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify(brand ? { brand } : {}),
    }),
    fallback,
  );
  return Number(body.data?.approved) || 0;
}
